import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Description as DocumentIcon,
  Assignment as TaskIcon,
  Assessment as AuditIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  Clear as ClearIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmin, useEntityAuditLogs } from '../../hooks/useSuperAdmin';
import { useAuth } from '../../context/AuthContext';
import { limitedAdminAPI } from '../../api/limitedAdminAPI';
import { apiClient } from '../../api/apiClient';

const SuperAdminDashboard = () => {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [auditLogsOpen, setAuditLogsOpen] = useState(false);
  const [userFilter, setUserFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [limitedAdminDialogOpen, setLimitedAdminDialogOpen] = useState(false);
  const [selectedUsersForAssignment, setSelectedUsersForAssignment] = useState([]);
  const [regularUsers, setRegularUsers] = useState([]);
  const [limitedAdmins, setLimitedAdmins] = useState([]);
  const [selectedLimitedAdmin, setSelectedLimitedAdmin] = useState('');
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Only load data when filters are applied
  const hasActiveFilters = userFilter || statusFilter;
  const shouldLoadData = hasActiveFilters;
  
  // Prepare filters object for documents and tasks
  const filters = {};
  if (userFilter) filters.userId = userFilter;
  if (statusFilter) filters.status = statusFilter;

  const {
    systemOverview,
    allUsers,
    allDocuments,
    allTaskAssignments,
    allAuditLogs,
    systemHealth,
    isLoading: dataLoading,
    error,
    refetch
  } = useSuperAdmin(filters);

  // Get entity audit logs when an entity is selected
  const { data: entityAuditLogs, isLoading: entityAuditLogsLoading } = useEntityAuditLogs(
    selectedEntity?.type,
    selectedEntity?.id,
    {}
  );

  // Load regular users and limited admins when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingUsers(true);
        console.log('=== FETCHING USERS AND LIMITED ADMINS ===');
        console.log('User token:', user?.token);
        console.log('User object:', user);
        console.log('User role:', user?.role);
        console.log('User permissions:', user?.role?.Permissions);
        
        // Fetch regular users (for assignment)
        console.log('=== FETCHING REGULAR USERS ===');
        const usersResponse = await limitedAdminAPI.getAllLimitedAdmins();
        console.log('Regular users response:', usersResponse);
        console.log('Regular users data:', usersResponse.data);
        const users = Array.isArray(usersResponse.data) ? usersResponse.data : [];
        console.log('Processed regular users:', users);
        setRegularUsers(users);
        
        // Fetch limited admins (for selection)
        console.log('=== FETCHING LIMITED ADMINS ===');
        const adminsResponse = await limitedAdminAPI.getLimitedAdmins();
        console.log('Limited admins response:', adminsResponse);
        console.log('Limited admins data:', adminsResponse.data);
        const admins = Array.isArray(adminsResponse.data) ? adminsResponse.data : [];
        console.log('Processed limited admins:', admins);
        setLimitedAdmins(admins);
      } catch (error) {
        console.error('Error fetching data:', error);
        console.error('Error details:', error.response?.data);
        console.error('Error status:', error.response?.status);
        setRegularUsers([]);
        setLimitedAdmins([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    
    if (user) {
      fetchData();
    }
  }, [user]);

  // Function to manually fetch user profile
  const fetchUserProfile = async () => {
    try {
      console.log('=== FETCHING USER PROFILE ===');
      const response = await apiClient.get(`/users/${user.id}`);
      console.log('User profile response:', response.data);
      // Update user in context
      login({ user: response.data, token: user.token });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  // Handle limited admin assignment
  const handleAssignToLimitedAdmin = () => {
    setSelectedUsersForAssignment([]);
    setSelectedLimitedAdmin('');
    setUserSearchTerm('');
    setLimitedAdminDialogOpen(true);
  };

  const handleUserSelect = (userId) => {
    setSelectedUsersForAssignment(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    const filteredUsers = getFilteredUsers();
    if (selectedUsersForAssignment.length === filteredUsers.length) {
      setSelectedUsersForAssignment([]);
    } else {
      setSelectedUsersForAssignment(filteredUsers.map(user => user.id));
    }
  };

  const getFilteredUsers = () => {
    if (!Array.isArray(regularUsers)) return [];
    return regularUsers.filter(user => 
      user.full_name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
  };

  const handleConfirmAssignment = async () => {
    if (!selectedLimitedAdmin || selectedUsersForAssignment.length === 0) return;

    try {
      setAssignmentLoading(true);
      await limitedAdminAPI.assignUsers(selectedLimitedAdmin, selectedUsersForAssignment);
      
      // Show success message
      alert(t('limitedadmin:assigned_successfully'));
      
      setLimitedAdminDialogOpen(false);
      setSelectedUsersForAssignment([]);
      setSelectedLimitedAdmin('');
      setUserSearchTerm('');
    } catch (error) {
      console.error('Error assigning users to limited admin:', error);
      alert(error.message || 'Failed to assign users');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setUserDetailsOpen(true);
  };

  const handleRestoreDocument = async (document) => {
    if (window.confirm(t('superadmin:confirm_restore_document', { title: document.title }))) {
      try {
        // Get token from user object stored in localStorage
        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        const token = user.token;
        
        if (!token) {
          alert('Authentication token not found. Please login again.');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/documents/${document.id}/restore`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          alert(t('superadmin:document_restored_successfully'));
          refetch(); // Refresh the data
        } else {
          const error = await response.json();
          alert(error.message || t('superadmin:failed_to_restore_document'));
        }
      } catch (error) {
        console.error('Error restoring document:', error);
        alert(t('superadmin:failed_to_restore_document'));
      }
    }
  };

  const handlePermanentDeleteDocument = async (document) => {
    if (window.confirm(t('superadmin:confirm_permanent_delete_document', { title: document.title }))) {
      try {
        // Get token from user object stored in localStorage
        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        const token = user.token;
        
        if (!token) {
          alert('Authentication token not found. Please login again.');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/documents/${document.id}/permanent`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          alert(t('superadmin:document_permanently_deleted_successfully'));
          refetch(); // Refresh the data
        } else {
          const error = await response.json();
          alert(error.message || t('superadmin:failed_to_permanently_delete_document'));
        }
      } catch (error) {
        console.error('Error permanently deleting document:', error);
        alert(t('superadmin:failed_to_permanently_delete_document'));
      }
    }
  };

  const handleViewAuditLogs = (entity, entityType) => {
    setSelectedEntity({ ...entity, type: entityType });
    setAuditLogsOpen(true);
  };

  const handleCloseAuditLogs = () => {
    setSelectedEntity(null);
    setAuditLogsOpen(false);
  };

  const handleViewDocumentFlow = (document) => {
    navigate(`/super-admin/document-flow/${document.id}`);
  };

  const handleExportData = (dataType) => {
    // TODO: Implement export functionality
    console.log('Export data type:', dataType);
  };

  const handleUserFilterChange = (event, newValue) => {
    setUserFilter(newValue?.id || '');
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  // Filter data based on selected user
  const getFilteredData = (data, dataType) => {
    if (!userFilter) return data;
    
    if (dataType === 'documents') {
      return data?.filter(item => 
        item.creator?.id === userFilter || 
        item.currentAssignee?.id === userFilter
      );
    } else if (dataType === 'tasks') {
      return data?.filter(item => 
        item.assignee?.id === userFilter || 
        item.assigner?.id === userFilter
      );
    }
    return data;
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'primary';
    }
  };

  const getLinearProgressColor = (status) => {
    switch (status) {
      case 'healthy': return 'primary';
      case 'warning': return 'secondary';
      case 'critical': return 'error';
      default: return 'primary';
    }
  };

  // Helper to get latest task assignment status for a document
  const getDocumentTaskStatus = (docId) => {
    if (!allTaskAssignments?.data?.taskAssignments || !Array.isArray(allTaskAssignments.data.taskAssignments)) {
      return null;
    }
    const assignment = allTaskAssignments.data.taskAssignments.find(t => t.document_id === docId);
    return assignment?.status || null;
  };

  // Helper to get status color for task assignment statuses
  const getTaskStatusColor = (status) => ({
    ASSIGNED: 'default',
    ACCEPTED: 'info',
    RECEIVED: 'info',
    IN_PROGRESS: 'primary',
    COMPLETED: 'success',
    REJECTED: 'error',
    REJECTED_RECEIPT: 'error',
    FORWARDED: 'warning',
    SKIPPED: 'secondary'
  }[status] || 'default');

  // Helper to get status label for task assignment statuses
  const getTaskStatusLabel = (status) => {
    const key = `superadmin:${status.toLowerCase()}`;
    const translated = t(key, status);
    return translated;
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />;
      case 'warning': return <WarningIcon />;
      case 'critical': return <ErrorIcon />;
      default: return <ErrorIcon />;
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('superadmin:error_loading_dashboard')}
        </Alert>
        <Alert severity="info" sx={{ mb: 2 }}>
          Error details: {error.message || 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('superadmin:super_admin_dashboard')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleAssignToLimitedAdmin}
            color="primary"
          >
            {t('limitedadmin:assign_users_to_limited_admin')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUserProfile}
            color="secondary"
          >
            Refresh Profile
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            {t('common:refresh')}
          </Button>
        </Box>
      </Box>

      {/* Debug Information */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Debug Info:</strong> User: {user?.email || 'Unknown'} | 
          Role: {user?.role?.name || 'No Role'} | 
          Permissions: {user?.role?.Permissions?.length || 0} | 
          Super Admin: {user?.role?.Permissions?.some(p => p.code === 'super_admin_access') ? 'Yes' : 'No'}
        </Typography>
      </Alert>


      {/* Overview Cards */}
      {systemOverview && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {systemOverview.totalUsers}
                    </Typography>
                    <Typography color="text.secondary">
                      {t('superadmin:total_users')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {systemOverview.activeUsers}
                    </Typography>
                    <Typography color="text.secondary">
                      {t('superadmin:active_users')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DocumentIcon color="info" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {systemOverview.totalDocuments}
                    </Typography>
                    <Typography color="text.secondary">
                      {t('superadmin:total_documents')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AuditIcon color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {systemOverview.totalAuditLogs}
                    </Typography>
                    <Typography color="text.secondary">
                      {t('superadmin:total_audit_logs')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={t('superadmin:documents')} />
            <Tab label={t('superadmin:tasks')} />
            <Tab label={t('superadmin:audit_logs')} />
          </Tabs>
        </Box>

        <CardContent>
          {/* General Filter Section - Applies to All Tabs */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <FilterIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('superadmin:general_filters')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('superadmin:filter_description')}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={allUsers?.data?.users || []}
                    getOptionLabel={(option) => option.full_name || option.username}
                    value={allUsers?.data?.users?.find(u => u.id === userFilter) || null}
                    onChange={handleUserFilterChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('superadmin:filter_by_user')}
                        variant="outlined"
                        size="small"
                        placeholder={t('superadmin:select_user_placeholder')}
                      />
                    )}
                    clearOnEscape
                    clearText={t('common:clear')}
                    noOptionsText={t('superadmin:no_users_found')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('superadmin:filter_by_status')}</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      label={t('superadmin:filter_by_status')}
                    >
                      <MenuItem value="">{t('common:all')}</MenuItem>
                      <MenuItem value="DRAFT">{t('superadmin:draft')}</MenuItem>
                      <MenuItem value="IN_PROGRESS">{t('superadmin:in_progress')}</MenuItem>
                      <MenuItem value="COMPLETED">{t('superadmin:completed')}</MenuItem>
                      <MenuItem value="REJECTED">{t('superadmin:rejected')}</MenuItem>
                      <MenuItem value="FORWARDED">{t('superadmin:forwarded')}</MenuItem>
                      <MenuItem value="ASSIGNED">{t('superadmin:assigned')}</MenuItem>
                      <MenuItem value="ACCEPTED">{t('superadmin:accepted')}</MenuItem>
                      <MenuItem value="REJECTED_RECEIPT">{t('superadmin:rejected_receipt')}</MenuItem>
                      <MenuItem value="ACTIVE">{t('superadmin:active')}</MenuItem>
                      <MenuItem value="INACTIVE">{t('superadmin:inactive')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setUserFilter('');
                        setStatusFilter('');
                      }}
                      startIcon={<ClearIcon />}
                      fullWidth
                    >
                      {t('common:clear_all')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => refetch()}
                      startIcon={<RefreshIcon />}
                      fullWidth
                    >
                      {t('common:refresh')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              {(userFilter || statusFilter) && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('superadmin:active_filters')}:
                  </Typography>
                  {userFilter && (
                    <Chip
                      label={`${t('superadmin:user')}: ${allUsers?.data?.users?.find(u => u.id === userFilter)?.full_name || allUsers?.data?.users?.find(u => u.id === userFilter)?.username}`}
                      onDelete={() => setUserFilter('')}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {statusFilter && (
                    <Chip
                      label={`${t('superadmin:status')}: ${statusFilter}`}
                      onDelete={() => setStatusFilter('')}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Documents Tab */}
          {activeTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('superadmin:all_documents')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExportData('documents')}
                >
                  {t('common:export')}
                </Button>
              </Box>
              {dataLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:title')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:reference')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:type')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:status')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:creator')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:created_at')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shouldLoadData ? (
                        getFilteredData(allDocuments?.data?.documents, 'documents')?.map((document) => (
                        <TableRow 
                          key={document.id} 
                          hover
                        >
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">
                              {document.title}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">
                              {document.reference_number}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Chip
                              label={document.type}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            {(() => {
                              const taskStatus = getDocumentTaskStatus(document.id);
                              if (!taskStatus) {
                                // Fallback to document status if no task assignment
                                return (
                                  <Chip
                                    label={t(`superadmin:${document.status.toLowerCase()}`) || document.status}
                                    size="small"
                                    color="info"
                                  />
                                );
                              }
                              
                              // Only use valid task assignment statuses
                              const validTaskStatuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'FORWARDED', 'ACCEPTED', 'RECEIVED', 'REJECTED_RECEIPT'];
                              if (!validTaskStatuses.includes(taskStatus)) {
                                return (
                                  <Chip
                                    label={t(`superadmin:${document.status.toLowerCase()}`) || document.status}
                                    size="small"
                                    color="info"
                                  />
                                );
                              }
                              
                              return (
                                <Chip
                                  label={getTaskStatusLabel(taskStatus)}
                                  size="small"
                                  color={getTaskStatusColor(taskStatus)}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">
                              {document.creator?.full_name || document.creator?.username || t('superadmin:unknown_user')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">
                              {document.created_at ? format(new Date(document.created_at), 'dd/MM/yyyy') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Tooltip title={t('superadmin:view_document_flow')}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewDocumentFlow(document);
                                  }}
                                  color="primary"
                                >
                                  <TimelineIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('superadmin:view_audit_logs')}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewAuditLogs(document, 'Document');
                                  }}
                                  color="secondary"
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              {document.is_deleted && (
                                <>
                                  <Tooltip title={t('superadmin:restore_document')}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRestoreDocument(document);
                                      }}
                                      color="success"
                                    >
                                      <RefreshIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('superadmin:permanent_delete_document')}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePermanentDeleteDocument(document);
                                      }}
                                      color="error"
                                    >
                                      <ClearIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        )) || []
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('superadmin:no_filters_applied')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Tasks Tab */}
          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('superadmin:all_tasks')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExportData('tasks')}
                >
                  {t('common:export')}
                </Button>
              </Box>
              {dataLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:document')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:assigned_to')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:assigned_by')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:status')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:priority')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:due_date')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:created_at')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shouldLoadData ? (
                        getFilteredData(allTaskAssignments?.data?.taskAssignments, 'tasks')?.map((task) => (
                        <TableRow 
                          key={task.id} 
                          hover 
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleViewAuditLogs(task, 'TaskAssignment')}
                        >
                          <TableCell>
                            <Typography variant="body2">
                              {task.document?.title || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {task.document?.reference_number || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {task.assignee?.full_name || task.assignee?.username || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {task.assigner?.full_name || task.assigner?.username || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={t(`superadmin:${task.status.toLowerCase()}`) || task.status}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={task.priority || '-'}
                              size="small"
                              color="secondary"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {task.due_date ? format(new Date(task.due_date), 'dd/MM/yyyy') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {task.assigned_at ? format(new Date(task.assigned_at), 'dd/MM/yyyy') : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        )) || []
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('superadmin:no_filters_applied')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Audit Logs Tab */}
          {activeTab === 2 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('superadmin:all_audit_logs')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExportData('audit_logs')}
                >
                  {t('common:export')}
                </Button>
              </Box>
              {dataLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:timestamp')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:user')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:action')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:document')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:entity_type')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:ip_address')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allAuditLogs?.data?.map((log) => (
                        <TableRow key={log.id} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {log.created_at ? format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {log.user?.full_name || log.user?.username || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.action}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {(() => {
                                // For Document entities, try to extract title from new_values or old_values
                                if (log.entity_type === 'Document') {
                                  try {
                                    // Try new_values first
                                    if (log.new_values) {
                                      const newValues = JSON.parse(log.new_values);
                                      if (newValues.title) return newValues.title;
                                    }
                                    // Try old_values if new_values doesn't have title
                                    if (log.old_values) {
                                      const oldValues = JSON.parse(log.old_values);
                                      if (oldValues.title) return oldValues.title;
                                    }
                                  } catch (e) {
                                    console.error('Error parsing audit log values:', e);
                                  }
                                }
                                
                                // For task-assignments, try to get document info from metadata
                                if (log.entity_type === 'task-assignments') {
                                  try {
                                    if (log.metadata) {
                                      const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
                                      
                                      // Check for document in metadata (from enhanced audit logging)
                                      if (metadata.document?.title) {
                                        return metadata.document.title;
                                      }
                                      
                                      // Legacy check for responseData structure
                                      if (metadata.responseData?.data?.document?.title) {
                                        return metadata.responseData.data.document.title;
                                      }
                                      
                                      // Check request body for document_title
                                      if (metadata.requestBody?.document_title) {
                                        return metadata.requestBody.document_title;
                                      }
                                    }
                                  } catch (e) {
                                    console.error('Error parsing task assignment metadata:', e);
                                  }
                                  
                                  // If we can't get document info, show a generic message
                                  return t('superadmin:task_assignment_document') || 'Task Assignment';
                                }
                                
                                // For TaskAssignment, try to get document info
                                if (log.entity_type === 'TaskAssignment' && log.entity_data?.document?.title) {
                                  return log.entity_data.document.title;
                                }
                                return '-';
                              })()}
                            </Typography>
                            {(() => {
                              // Show reference number if available
                              if (log.entity_type === 'Document') {
                                try {
                                  let refNumber = null;
                                  if (log.new_values) {
                                    const newValues = JSON.parse(log.new_values);
                                    refNumber = newValues.reference_number;
                                  }
                                  if (!refNumber && log.old_values) {
                                    const oldValues = JSON.parse(log.old_values);
                                    refNumber = oldValues.reference_number;
                                  }
                                  if (refNumber) {
                                    return (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {refNumber}
                                      </Typography>
                                    );
                                  }
                                } catch (e) {
                                  console.error('Error parsing reference number:', e);
                                }
                              }
                              return null;
                            })()}
                            
                            {(() => {
                              // Show reference number for task-assignments if available in metadata
                              if (log.entity_type === 'task-assignments') {
                                try {
                                  if (log.metadata) {
                                    const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
                                    let refNumber = null;
                                    
                                    // Check for reference number in metadata (from enhanced audit logging)
                                    if (metadata.document?.reference_number) {
                                      refNumber = metadata.document.reference_number;
                                    } else if (metadata.responseData?.data?.document?.reference_number) {
                                      refNumber = metadata.responseData.data.document.reference_number;
                                    } else if (metadata.requestBody?.document_reference) {
                                      refNumber = metadata.requestBody.document_reference;
                                    }
                                    
                                    if (refNumber) {
                                      return (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          {refNumber}
                                        </Typography>
                                      );
                                    }
                                  }
                                } catch (e) {
                                  console.error('Error parsing task assignment reference number:', e);
                                }
                              }
                              return null;
                            })()}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {log.entity_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {log.ip_address || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog
        open={userDetailsOpen}
        onClose={() => setUserDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('superadmin:user_details')}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:name')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.full_name || selectedUser.username}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:email')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:role')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.role?.name || 'No Role'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:department')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.department?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:status')}
                  </Typography>
                  <Chip
                    label={selectedUser.is_active ? t('superadmin:active') : t('superadmin:inactive')}
                    color={selectedUser.is_active ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common:created_at')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedUser.created_at ? format(new Date(selectedUser.created_at), 'dd/MM/yyyy HH:mm:ss') : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDetailsOpen(false)}>
            {t('common:close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Entity Audit Logs Modal */}
      <Dialog
        open={auditLogsOpen}
        onClose={handleCloseAuditLogs}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {t('superadmin:audit_logs_for')} {selectedEntity?.title || selectedEntity?.document?.title || selectedEntity?.id}
        </DialogTitle>
        <DialogContent>
          {entityAuditLogsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: 'right' }}>{t('common:timestamp')}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{t('common:user')}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{t('common:action')}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{t('common:document')}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{t('common:description')}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{t('common:ip_address')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entityAuditLogs?.data?.auditLogs?.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {log.created_at ? format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss') : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.user?.full_name || log.user?.username || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // For Document entities, try to extract title from new_values or old_values
                            if (log.entity_type === 'Document') {
                              try {
                                // Try new_values first
                                if (log.new_values) {
                                  const newValues = JSON.parse(log.new_values);
                                  if (newValues.title) return newValues.title;
                                }
                                // Try old_values if new_values doesn't have title
                                if (log.old_values) {
                                  const oldValues = JSON.parse(log.old_values);
                                  if (oldValues.title) return oldValues.title;
                                }
                              } catch (e) {
                                console.error('Error parsing audit log values:', e);
                              }
                            }
                            // For task-assignments, try to get document info from metadata
                            if (log.entity_type === 'task-assignments') {
                              try {
                                if (log.metadata) {
                                  const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
                                  
                                  // Check for document in metadata (from enhanced audit logging)
                                  if (metadata.document?.title) {
                                    return metadata.document.title;
                                  }
                                  
                                  // Legacy check for responseData structure
                                  if (metadata.responseData?.data?.document?.title) {
                                    return metadata.responseData.data.document.title;
                                  }
                                  
                                  // Check request body for document_title
                                  if (metadata.requestBody?.document_title) {
                                    return metadata.requestBody.document_title;
                                  }
                                }
                              } catch (e) {
                                console.error('Error parsing task assignment metadata:', e);
                              }
                              
                              return t('superadmin:task_assignment_document') || 'Task Assignment';
                            }
                            
                            // For TaskAssignment, try to get document info
                            if (log.entity_type === 'TaskAssignment' && log.entity_data?.document?.title) {
                              return log.entity_data.document.title;
                            }
                            return '-';
                          })()}
                        </Typography>
                        {(() => {
                          // Show reference number if available
                          if (log.entity_type === 'Document') {
                            try {
                              let refNumber = null;
                              if (log.new_values) {
                                const newValues = JSON.parse(log.new_values);
                                refNumber = newValues.reference_number;
                              }
                              if (!refNumber && log.old_values) {
                                const oldValues = JSON.parse(log.old_values);
                                refNumber = oldValues.reference_number;
                              }
                              if (refNumber) {
                                return (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {refNumber}
                                  </Typography>
                                );
                              }
                            } catch (e) {
                              console.error('Error parsing reference number:', e);
                            }
                          }
                          return null;
                        })()}
                        
                        {(() => {
                          // Show reference number for task-assignments if available in metadata
                          if (log.entity_type === 'task-assignments') {
                            try {
                              if (log.metadata) {
                                const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
                                let refNumber = null;
                                
                                // Check for reference number in metadata (from enhanced audit logging)
                                if (metadata.document?.reference_number) {
                                  refNumber = metadata.document.reference_number;
                                } else if (metadata.responseData?.data?.document?.reference_number) {
                                  refNumber = metadata.responseData.data.document.reference_number;
                                } else if (metadata.requestBody?.document_reference) {
                                  refNumber = metadata.requestBody.document_reference;
                                }
                                
                                if (refNumber) {
                                  return (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {refNumber}
                                    </Typography>
                                  );
                                }
                              }
                            } catch (e) {
                              console.error('Error parsing task assignment reference number:', e);
                            }
                          }
                          return null;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.ip_address || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAuditLogs}>
            {t('common:close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Limited Admin Assignment Dialog */}
      <Dialog
        open={limitedAdminDialogOpen}
        onClose={() => setLimitedAdminDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('limitedadmin:assign_users_to_limited_admin')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Limited Admin Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('limitedadmin:select_limited_admin')}</InputLabel>
              <Select
                value={selectedLimitedAdmin}
                onChange={(e) => setSelectedLimitedAdmin(e.target.value)}
                label={t('limitedadmin:select_limited_admin')}
                disabled={loadingUsers}
              >
                {loadingUsers ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Loading limited admins...</Typography>
                    </Box>
                  </MenuItem>
                ) : Array.isArray(limitedAdmins) && limitedAdmins.length > 0 ? (
                  limitedAdmins.map((admin) => (
                    <MenuItem key={admin.id} value={admin.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon />
                        <Box>
                          <Typography variant="body2">
                            {admin.full_name || admin.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {admin.email}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No limited admins found
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {selectedLimitedAdmin && (
              <>
                <Divider sx={{ mb: 2 }} />
                
                {/* User Search */}
                <TextField
                  fullWidth
                  placeholder={t('limitedadmin:search_users')}
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  sx={{ mb: 2 }}
                />

                {/* Select All Users */}
                <Box display="flex" alignItems="center" mb={2}>
                  <Checkbox
                    checked={selectedUsersForAssignment.length > 0 && selectedUsersForAssignment.length === getFilteredUsers().length}
                    indeterminate={selectedUsersForAssignment.length > 0 && selectedUsersForAssignment.length < getFilteredUsers().length}
                    onChange={handleSelectAllUsers}
                  />
                  <Typography variant="body2">
                    {selectedUsersForAssignment.length > 0 
                      ? t('limitedadmin:selected_count', { count: selectedUsersForAssignment.length })
                      : t('limitedadmin:select_all_users')
                    }
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Users List */}
                <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  {getFilteredUsers().map((user) => (
                    <Box
                      key={user.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        borderBottom: '1px solid #f0f0f0',
                        '&:hover': { bgcolor: '#f5f5f5' }
                      }}
                    >
                      <Checkbox
                        checked={selectedUsersForAssignment.includes(user.id)}
                        onChange={() => handleUserSelect(user.id)}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <PersonIcon color="action" />
                        <Box>
                          <Typography variant="body2">
                            {user.full_name || user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      {selectedUsersForAssignment.includes(user.id) && (
                        <CheckCircleIcon color="success" size="small" />
                      )}
                    </Box>
                  ))}
                </Box>

                {getFilteredUsers().length === 0 && (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t('limitedadmin:no_users_found')}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setLimitedAdminDialogOpen(false)}
            disabled={assignmentLoading}
          >
            {t('common:cancel')}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleConfirmAssignment}
            disabled={!selectedLimitedAdmin || selectedUsersForAssignment.length === 0 || assignmentLoading}
            startIcon={assignmentLoading ? <CircularProgress size={16} /> : <PersonAddIcon />}
          >
            {assignmentLoading ? t('common:assigning') : t('common:assign')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminDashboard;
